
import { DmnModel, DataType, ColumnType } from '../types';

/**
 * Validates a single cell value based on its data type and whether it's an input or output.
 */
export const validateCell = (value: string, dataType: DataType, columnType: ColumnType): boolean => {
  const v = value.trim();
  
  // Empty cell or dash is generally considered a "wildcard" or "not set", which is valid in DMN context
  if (v === '' || v === '-') return true;

  switch (dataType) {
    case 'string':
      // Strings are generally always valid, but might require quotes in some FEEL contexts.
      // For this editor, we'll allow anything.
      return true;

    case 'boolean':
      return ['true', 'false'].includes(v.toLowerCase());

    case 'number':
      // 1. Check for simple numeric literals
      if (!isNaN(Number(v)) && v !== '') return true;
      
      // 2. If it's an input, allow FEEL expressions
      if (columnType === 'input') {
        // Comparisons: > 10, <= 5.5, etc.
        if (/^(>|<|>=|<=)\s*-?\d+(\.\d+)?$/.test(v)) return true;
        
        // Ranges: [1..10], (0..100], etc.
        if (/^(\[|\()\s*-?\d+(\.\d+)?\s*\.\.\s*-?\d+(\.\d+)?\s*(\]|\))$/.test(v)) return true;
        
        // List of values: 1, 2, 3
        if (/^-?\d+(\.\d+)?(\s*,\s*-?\d+(\.\d+)?)*$/.test(v)) return true;
      }
      
      return false;

    case 'date':
      // Basic YYYY-MM-DD validation
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(v)) return false;
      const date = new Date(v);
      return date instanceof Date && !isNaN(date.getTime());

    default:
      return true;
  }
};

/**
 * Validates the entire DMN model.
 * Returns a map of invalid cell IDs and a global validity flag.
 */
export const validateModel = (model: DmnModel) => {
  const invalidCells: Record<string, boolean> = {};
  let totalInvalid = 0;

  model.rules.forEach(rule => {
    model.columns.forEach(col => {
      const value = rule.cells[col.id] || '';
      const isValid = validateCell(value, col.dataType, col.type);
      if (!isValid) {
        invalidCells[`${rule.id}-${col.id}`] = true;
        totalInvalid++;
      }
    });
  });

  return {
    invalidCells,
    isValid: totalInvalid === 0,
    errorCount: totalInvalid
  };
};
